import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

console.log('BACKEND_URL:', BACKEND_URL);
console.log('API:', API);

const Blog = () => {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        console.log('BACKEND_URL:', BACKEND_URL);
        console.log('API:', API);
        console.log('Fetching from:', `${API}/blog/articles`);
        
        const response = await axios.get(`${API}/blog/articles`);
        console.log('Response status:', response.status);
        console.log('Articles received:', response.data);
        setArticles(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching articles:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError(`Failed to load articles: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [API]);

  return (
    <div className="min-h-screen pt-20" data-testid="blog-page">
      <Helmet>
        <title>{i18n.language === 'tr' ? 'Güvenlik Blog | SULH' : 'Security Blog | SULH'}</title>
        <meta name="description" content={i18n.language === 'tr' ? 'Siber güvenlik haberleri, tehdit analizi ve en iyi uygulamalar.' : 'Cybersecurity news, threat analysis and best practices.'} />
        <link rel="canonical" href="https://www.sulh.tech/blog" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="blog-title">
            {t('blog.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto" data-testid="blog-subtitle">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Blog Articles */}
      <section className="section-padding" data-testid="articles-section">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
              <p className="text-gray-400 mt-4">{t('blog.loading')}</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400">{t('blog.noArticles')}</p>
            </div>
          )}

          {!loading && !error && articles.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {console.log('Rendering articles:', articles.length, 'articles')}
              {articles.map((article, index) => (
                <article
                  key={index}
                  className="glass-card p-6 flex flex-col"
                  data-testid={`article-${index}`}
                >
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 text-xs font-medium text-blue-400 bg-blue-400/10 rounded-full">
                      {article.category}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-3 flex-grow" data-testid={`article-title-${index}`}>
                    {article.title}
                  </h2>
                  <p className="text-gray-400 mb-4">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-2">
                      <User size={14} />
                      <span>{article.source}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} />
                      <span>{article.published}</span>
                    </div>
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                      data-testid={`read-more-${index}`}
                    >
                      {t('blog.readMore')}
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="section-padding bg-slate-900/50" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="newsletter-title">
            Subscribe to Security Updates
          </h2>
          <p className="text-gray-300 mb-8">
            Get the latest cybersecurity insights delivered directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
              data-testid="newsletter-email-input"
            />
            <button className="btn-primary whitespace-nowrap" data-testid="newsletter-subscribe-button">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
